import { i18n } from '../../../shared/i18n/i18n.js';



/**
 * compareRows — registry + builders for the FavoritesView comparison columns.
 *
 * ## How to add a new row to the comparison columns
 *
 * 1. Add an ID to `COMPARE_ROW_ID` in `constants/compareRowIds.js`.
 * 2. Add a settings flag in `buildCompareSettings()` below.
 * 3. Add one entry to `COMPARE_ROW_REGISTRY`.
 * For simple data points, {label: value} use a generic entry that reads the value from the product based on the ID. 
 * For rows that need value formatting (price, area, exposition, etc.) a custom render function can be provided.
 * Rows that require a custom component (CTA buttons, status badges) must pass `component` key to be handled in `CompareColumnItem.jsx`.
 * 
 */

import {
	formatProductPrice,
} from '../../../shared/utils/index.js';
import {
	formatProductArea,
	formatSquareMeters,
	formatTier,
	formatProductExposition,
	formatProductInsolation,
	formatProductInsolationRange,
	formatOutdoorCompartmentList,
} from '../../../shared/utils/productFormatters.js';

import { COMPARE_ROW_ID } from '../constants/compareRowIds.js';

// ── Registry ──────────────────────────────────────────────────────────────────

/**
 * COMPARE_ROW_REGISTRY — declarative list of all possible comparison rows.
 *
 * Each entry:
 *   id           — matches COMPARE_ROW_ID
 *   labelKey     — i18n key for the DataRow label
 *   isAvailable  — (settings) => boolean — capability/settings gate
 *   getValue     — (product, t, context) => string|object|null — extracts + formats the value;
 *                  returning null silently drops the row from the rendered column
 *   getTooltip   — (product, t, context) => string|object|null — optional; if present and non-null,
 *                  a (i) icon is rendered next to the label via TooltipInfo, showing the tooltip text on hover.
 *	 component    — optional, for rows that require a custom React component instead of a DataRow (e.g. status badge, CTA button, etc.)
 * 
 * GROUP PLACEHOLDERS for CMS-defined data groups (e.g. additional_unit_areas) that can be toggled as a whole but whose individual items are not known in advance and must be derived from the product + panelSettings at runtime. These entries are handled in buildCompareColumnRows() with custom logic.
 * 
 * Registry order determines default render order.
 * Final render order is determined by the `order` property in panel settings (favorites_compare.rows).
 * context shape: { compartmentTypes: object|null, panelSettings: object|null }
*/

export const COMPARE_ROW_REGISTRY = [
	{
		id: COMPARE_ROW_ID.OPEN_CTA,
		labelKey: null,
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.OPEN_CTA],
		getValue: ({ t }) => t('product_category_strings.sheet_product'),
		component: 'action',
	},
	{
		id: COMPARE_ROW_ID.PLAN_THUMB,
		labelKey: null,
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.PLAN_THUMB],
		getValue: () => null,
		component: 'plan_thumb',
	},
	{
		id: COMPARE_ROW_ID.STATUS,
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.STATUS],
		getValue: ({ product }) => product.status ?? '—',
		component: 'status',
	},

	// Generic DataRow entries, some with custom value formatting
	{
		id: COMPARE_ROW_ID.INVESTMENT,
		labelKey: 'common.investment',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.INVESTMENT],
		getValue: ({ product }) => product?.investment_name ?? '—',
	},
	{
		id: COMPARE_ROW_ID.PRODUCT_CATEGORY,
		labelKey: 'favorites.fav_category',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.PRODUCT_CATEGORY],
		getValue: ({ product, plural }) => plural(`product_category_strings.${product.product_category}`, 1) ?? '—',
	},
	{
		id: COMPARE_ROW_ID.PRICE,
		labelKey: 'common.price',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.PRICE],
		getValue: ({ product, context }) => {
			const { pricesVisible, pricesSoldVisible, pricesReservedVisible } = context?.pricesVisibility ?? {};
			const { displayPrice, strikethroughPrice } = formatProductPrice(product, { pricesVisible, pricesSoldVisible, pricesReservedVisible });
			return { displayPrice, strikethroughPrice };
		},
		component: 'price',
	},
	{
		id: COMPARE_ROW_ID.PRICE_M2,
		labelKey: 'common.price_m2',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.PRICE_M2],
		getValue: ({ product, context }) => {
			const { pricesVisible, pricesSoldVisible, pricesReservedVisible } = context?.pricesVisibility ?? {};
			const { displayPrice, strikethroughPrice } = formatProductPrice(product, { pricesVisible, pricesSoldVisible, pricesReservedVisible, sqm: true });
			return { displayPrice, strikethroughPrice };
		},
		component: 'price',
	},
	{
		id: COMPARE_ROW_ID.AREA,
		labelKey: 'common.area_m2',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.AREA],
		getValue: ({ product }) => formatProductArea(product) ?? '—',
	},
	// {id: COMPARE_ROW_ID.AREA_PERCENTILE,}, //--------------------------------------------------------------------------------
	// {id: COMPARE_ROW_ID.HEIGHT,},  //----------------------------------------------------------------------------------------
	// {id: COMPARE_ROW_ID.LOT_AREA,},  //--------------------------------------------------------------------------------------

	// GROUP PLACEHOLDER FOR CMS-DEFINED DATA GROUP. CUSTOM HANDLED IN buildCompareColumnRows().
	{
		id: COMPARE_ROW_ID.ADDITIONAL_AREAS_GROUP,
		isGroupPlaceholder: true,
		cmsKey: 'additional_unit_areas',
		dataKey: 'area',
		idPrefix: 'additional_area_',
		getValue: ({ item, product, idx }) => {
			const productDataArray = product.additional_unit_areas;
			if (!Array.isArray(productDataArray)) return null;
			const matchedItem = productDataArray.find((p) => {
				if (item.label) return p.label === item.label;

				return productDataArray.indexOf(p) === idx;
			});

			const prefix = item?.prefix?.length ? item.prefix : null;

			const formattedValue = matchedItem?.value ? formatSquareMeters(matchedItem.value) : null
			const prefixedValue = prefix ? `${prefix} ${formattedValue}` : formattedValue;
			return prefixedValue;
		},
	},

	{
		id: COMPARE_ROW_ID.TURN_KEY_STYLE,
		labelKey: 'product.turn_key_style',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.TURN_KEY_STYLE],
		getValue: ({product, context }) => {
			const styles = context?.ADDITIONAL_AREAS_GROUPpanelSettings?.turn_key_styles?.styles ?? []
			const match = styles.find((s) => s.label === product?.turn_key_style)
			return match?.label ?? '—'
		},
		getTooltip: ({product, context }) => {
			const styles = context?.panelSettings?.turn_key_styles?.styles ?? []
			const match = styles.find((s) => s.label === product?.turn_key_style)
			return match?.tooltip ? { ...match.tooltip } : null;
		},
	},
	// {id: COMPARE_ROW_ID.FLAGS,}, //----------------------------------------------------------------------------------------
	{
		id: COMPARE_ROW_ID.LEVELS,
		labelKey: 'common.levels',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.LEVELS],
		getValue: ({ product }) => product?.levels != null ? String(product.levels) : null,
	},
	{
		id: COMPARE_ROW_ID.EXPOSITION,
		labelKey: 'common.exposition',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.EXPOSITION],
		getValue: ({ product, t }) => formatProductExposition(product, t) ?? '—',
		getTooltip: ({ t }) => ({
			text: t('product.tooltip_exposition') || null,
			text_html: null,
		}),
	},
	{
		id: COMPARE_ROW_ID.INSOLATION_HOURS,
		labelKey: 'common.insolation_hours',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.INSOLATION_HOURS],
		getValue: ({ product }) => formatProductInsolation(product) ?? '—',
		getTooltip: ({ t }) => ({
			text: t('product.insolation_hours_tooltip') || null,
			text_html: null,
		}),
	},
	{
		id: COMPARE_ROW_ID.INSOLATION_RANGE,
		labelKey: 'common.insolation_range',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.INSOLATION_RANGE],
		getValue: ({ product }) => formatProductInsolationRange(product) ?? '—',
		getTooltip: ({ t }) => ({
			text: t('product.insolation_range_tooltip') || null,
			text_html: null,
		}),
	},
	{
		id: COMPARE_ROW_ID.ROOMS,
		labelKey: 'common.rooms',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.ROOMS],
		getValue: ({ product }) => product?.rooms != null ? String(product.rooms) : null,
	},
	{
		id: COMPARE_ROW_ID.OUTDOOR_AREAS,
		labelKey: 'favorites.fav_outdoor_areas',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.OUTDOOR_AREAS],
		getValue: ({ product, t, context }) => {
			const compartmentTypes = context?.compartmentTypes ?? null;
			return formatOutdoorCompartmentList(product, compartmentTypes, t) ?? '—';
		},
	},
	// {id: COMPARE_ROW_ID.SPECIAL_AREAS,},

	// GROUP PLACEHOLDER FOR CMS-DEFINED DATA GROUP. CUSTOM HANDLED IN buildCompareColumnRows().
	{
		id: COMPARE_ROW_ID.ADDITIONAL_DATA_GROUP,
		isGroupPlaceholder: true,
		cmsKey: 'additional_unit_data',
		dataKey: 'data',
		idPrefix: 'additional_data_',
		getValue: ({ item, product, idx }) => {
			const productDataArray = product.additional_unit_data;
			if (!Array.isArray(productDataArray)) return null;
			const matchedItem = productDataArray.find((p) => {
				if (item.label) return p.label === item.label;

				return productDataArray.indexOf(p) === idx;
			});

			return matchedItem?.value ?? '—';
		},
	},

	{
		id: COMPARE_ROW_ID.STAGE,
		labelKey: 'common.stage',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.STAGE],
		getValue: ({ product }) => product?.stage_name ?? '—',
	},
	{
		id: COMPARE_ROW_ID.BUILDING,
		labelKey: 'common.building',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.BUILDING],
		getValue: ({ product }) => product?.building_name ?? '—',
	},
	{
		id: COMPARE_ROW_ID.GATE,
		labelKey: 'common.gate',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.GATE],
		getValue: ({ product }) => product?.gate ?? '—',
	},
	{
		id: COMPARE_ROW_ID.FLOOR,
		labelKey: 'common.floor',
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.FLOOR],
		getValue: ({ product }) => formatTier(product),
	},

	// {id: COMPARE_ROW_ID.ENERGY_CLASS,},  //----------------------------------------------------------------------------------------------------
	// {id: COMPARE_ROW_ID.ENERGY_USEFUL,},  //----------------------------------------------------------------------------------------------------
	// {id: COMPARE_ROW_ID.ENERGY_FINAL,},  //----------------------------------------------------------------------------------------------------
	// {id: COMPARE_ROW_ID.ENERGY_PRIMARY,},  //----------------------------------------------------------------------------------------------------
	// {id: COMPARE_ROW_ID.ENERGY_RENEWABLE_SHARE,},  //----------------------------------------------------------------------------------------------------
	// {id: COMPARE_ROW_ID.ENERGY_CO2_EMISSIONS,},  //----------------------------------------------------------------------------------------------------
	// {id: COMPARE_ROW_ID.ENERGY_HEATING_COST_NOW,},  //----------------------------------------------------------------------------------------------------
	// {id: COMPARE_ROW_ID.ENERGY_HEATING_COST_10Y,},  //----------------------------------------------------------------------------------------------------

	// Inquiry button at the bottom
	{
		id: COMPARE_ROW_ID.INQUIRY_CTA,
		labelKey: null,
		isVisible: (visibilityMap) => visibilityMap[COMPARE_ROW_ID.INQUIRY_CTA],
		getValue: ({ t }) => t('common.send_inquiry'),
		component: 'cta',
	}
]


// ── Builders ──────────────────────────────────────────────────────────────────
// ── Visibility map builder ──────────────────────────────────────────────────────────

/**
 * buildCompareRowVisibility — derives display flags for the Compare column from
 * real panelSettings keys + capabilities + buildings.
 *
 * CMS overrides live under `panelSettings.favorites_compare`.
 * All per-field flags default to `true` when the key is absent and data is available.
 * Fields with a broader gate (capability or project config) are additionally
 * gated here regardless of the per-field toggle.
 *
 * @param {object|null} panelSettings
 * @param {object}      appConfig — from AppConfig
 * @param {boolean}     arePricesVisible - from usePriceVisibility
 * @param {object[]}    buildings    — from CatalogContext
 * @returns {object}
 */
export function buildCompareRowVisibility(panelSettings, appConfig, arePricesVisible, buildings) {
	const isMultiBuilding = Array.isArray(buildings) && buildings.length > 1
	const cmsRowsConfig = panelSettings?.favorites_compare?.rows ?? [];
	const baseMap = cmsRowsConfig.reduce((acc, item) => {
		acc[item.id] = item.is_visible;
		return acc;
	}, {});

	const safeguards = {
		// CORE DATA FIELDS — render even if whole CMS config is missing, only hide if explicitly toggled off (is_visible: false)
		[COMPARE_ROW_ID.AREA]: (original) => original !== false,
		[COMPARE_ROW_ID.FLOOR]: (original) => original !== false,
		[COMPARE_ROW_ID.ROOMS]: (original) => original !== false,
		[COMPARE_ROW_ID.STATUS]: (original) => original !== false,

		// CAPABILITY-GATED FIELDS — only render if capability is present + data is available
		[COMPARE_ROW_ID.BUILDING]: 
			(original) => original === true && isMultiBuilding,
		[COMPARE_ROW_ID.INSOLATION_HOURS]: 
			(original) => original === true && !!(appConfig?.apartments?.floorplan_insolation), //&& !!(capabilities?.hasInsolationCommissioned),
		[COMPARE_ROW_ID.INSOLATION_RANGE]: 
			(original) => original === true && !!(appConfig?.apartments?.floorplan_insolation), //&& !!(capabilities?.hasInsolationCommissioned),
		[COMPARE_ROW_ID.PRICE]: 
			(original) => original === true && arePricesVisible,
		[COMPARE_ROW_ID.PRICE_M2]: 
			(original) => original === true && arePricesVisible,
		[COMPARE_ROW_ID.TURN_KEY_STYLE]: 
			(original) => original === true && !!(panelSettings?.turn_key_styles?.styles?.some(s => s.in_use)),
	};

	for (const [id, conditionFn] of Object.entries(safeguards)) {
		if (id in baseMap) {
		baseMap[id] = conditionFn(baseMap[id]);
		}
	}

	return baseMap;
}

// ── Main builder ──────────────────────────────────────────────────────────────

/**
 * buildCompareColumnRows — builds the column rows array for the FavoritesView compare columns.
 *
 * Iterates COMPARE_ROWS_REGISTRY, filters by settings flags, and maps each
 * entry to a `{ label, value, tooltip? }` object. Entries whose `getValue`
 * returns null are silently dropped.
 *
 * @param {object|null} product
 * @param {object}      visibilityMap  				— from buildCompareRowVisibility()
 * @param {function}    t         					— translation function from useI18n
 * @param {function}    plural         				— translation function from useI18n
 * @param {object}      context
 * @param {object|null} context.compartmentTypes 	— full compartments.json or compartments_types map
 * @param {object|null} context.panelSettings    	— passed through for dynamic tooltip/value lookups
 * @param {object|null} context.pricesVisibility 	— passed through for price formatting
 * @returns {Array<{label: string, value: string|object|null, component?: string, tooltip?: string}>}
 */
export function buildCompareColumnRows(product, visibilityMap, t, plural, context) {
	if (!product) return [];
	const rows = [];

	for (const entry of COMPARE_ROW_REGISTRY) {
		// Dynamic data group process for CMS defined data points i.e. (additional_unit_areas or _data)
		if (entry.isGroupPlaceholder) {
			const isGroupVisible = visibilityMap[entry.id] !== false;
			if (isGroupVisible) {
				const rawItems = context?.panelSettings?.[entry.cmsKey]?.[entry.dataKey] || [];
				
				rawItems.forEach((item, idx) => {
					if (item.is_visible === false) return;
					const value = entry.getValue({item, product, idx});
					rows.push({
						id: `${entry.idPrefix}${idx}`,
						groupId: entry.id,
						label: item.label || '',
						value,
						tooltip: item?.tooltip ? {...item.tooltip} : null,
					});
				});
			}
		continue;
		}

		// Standard row process
		if (!entry?.isVisible(visibilityMap)) continue;
		const value = entry.getValue({product, t, plural, context});
    	const rawTooltip = entry.getTooltip ? entry.getTooltip({product, t, plural, context}) : undefined
		rows.push({ 
			id: entry.id, 
			label: entry.labelKey ? t(entry.labelKey) : null, 
			value, 
			component: entry.component, 
      		tooltip: rawTooltip ? { ...rawTooltip } : null,
		});
	}

	// Sorting
	const cmsRowsConfig = context?.panelSettings?.favorites_compare?.rows ?? [];
	const cmsOrderMap = Array.isArray(cmsRowsConfig)
		? cmsRowsConfig.reduce((acc, item) => {
			if (item?.id && typeof item?.order === 'number') {
			acc[item.id] = item.order;
			}
			return acc;
		}, {})
		: {};
	const registryOrderMap = COMPARE_ROW_REGISTRY.reduce((acc, entry, index) => {
		acc[entry.id] = index;
		return acc;
	}, {});

	// Sort by CMS [order] with REGISTRY tiebreaker
	rows.sort((rowA, rowB) => {
		// Check if row comes from dynamic group
		const lookupIdA = rowA.groupId || rowA.id;
		const lookupIdB = rowB.groupId || rowB.id;
		
		const orderA = cmsOrderMap[lookupIdA] ?? Infinity;
		const orderB = cmsOrderMap[lookupIdB] ?? Infinity;

		if (orderA !== orderB) {
		return orderA - orderB;
		}

		const fallbackA = registryOrderMap[rowA.id] ?? 0;
		const fallbackB = registryOrderMap[rowB.id] ?? 0;

		return fallbackA - fallbackB;
	});

	return rows;
}